// 2 · Waiter order pad (OrderPad.dc.html; iPad 1180×820, responsive).
// Lines are priced on the server when they are added; the pad shows what the
// session view answers (draft / rounds / totals) and never a total of its own.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, errorText, newKey } from '../lib/api';
import { clock, joinNotes, modifierText, money, nowClock, plural, shortName, splitNotes } from '../lib/format';
import { useLive } from '../lib/useLive';
import { useDiningSession } from '../lib/session';
import { useSoundPref } from '../lib/sound';
import { C, COURSES, courseLabel, FONT_TIGHT, LINE_CHIP } from '../theme/tokens';
import { useApproval } from '../components/ApprovalDialog';
import { AlertBell, AlertCenter, alertsEnabledFor, useAlerts } from '../components/AlertCenter';
import { LeftRail } from '../components/Shell';
import {
  Btn, Chip, Clock, Empty, ErrorNote, FullError, inputStyle, Modal, ModalFoot, ModalHead, Segmented, Skeleton, Stepper,
  StreamBanner, useToast,
} from '../components/ui';
import { IconAlert, IconCheck, IconClose, IconLock, IconOrder, IconPlus, IconSearch, IconSend, IconSwap } from '../components/icons';

// ------------------------------------------------------------------ menu tile

const TILE_BG = ['#2E2925', '#3A2B22', '#23302E', '#2B2522', '#3B2A1F', '#2C2724', '#26302A', '#33281F'];

function Plate({ item, height = 118 }) {
  if (item.image_url) {
    return <img src={item.image_url} alt="" loading="lazy" style={{ width: '100%', height, objectFit: 'cover', display: 'block' }} />;
  }
  const bg = TILE_BG[item.id % TILE_BG.length];
  const letters = item.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return (
    <div aria-hidden="true" style={{ height, background: `radial-gradient(circle at 30% 20%, rgba(255,255,255,.08), transparent 55%), ${bg}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 84, height: 84, borderRadius: '50%', background: 'rgba(255,255,255,.06)', boxShadow: 'inset 0 0 0 10px rgba(255,255,255,.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.72)', fontFamily: FONT_TIGHT, fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px' }}>{letters}</div>
    </div>
  );
}

function MenuTile({ item, onOpen, onQuickAdd, canAdd }) {
  const out = !item.available;
  const tag = item.is_popular ? 'Popular' : item.is_vegan ? 'Vegan' : item.is_vegetarian ? 'Vegetarian' : item.is_gluten_free ? 'Gluten free' : null;
  return (
    <article style={{ background: '#FFFFFF', borderRadius: 18, border: '1px solid #EEE8E1', overflow: 'hidden', display: 'flex', flexDirection: 'column', opacity: out ? 0.6 : 1 }}>
      <button type="button" onClick={() => !out && onOpen(item)} disabled={out || !canAdd} aria-label={`${item.name} options`}
        style={{ border: 'none', padding: 0, background: 'transparent', position: 'relative', textAlign: 'left' }}>
        <Plate item={item} />
        {tag && <span style={{ position: 'absolute', left: 10, top: 10, height: 24, padding: '0 9px', borderRadius: 12, background: 'rgba(255,255,255,.92)', color: C.ink, fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>{tag}</span>}
        {out && <span style={{ position: 'absolute', inset: 0, background: 'rgba(28,25,23,.55)', color: '#FFFFFF', fontSize: 14, fontWeight: 800, letterSpacing: '0.08em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>SOLD OUT</span>}
      </button>
      <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, lineHeight: 1.25 }}>{item.name}</h3>
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.4, color: C.muted, minHeight: 35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.description || (item.modifier_groups.length ? item.modifier_groups.map((g) => g.name).join(' · ') : '')}</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
          <span style={{ fontSize: 17, fontWeight: 800 }}>{money(item.price_cents)}</span>
          {canAdd && (
            <button type="button" className="c29-press" onClick={() => (item.modifier_groups.length ? onOpen(item) : onQuickAdd(item))} disabled={out} aria-label={`Add ${item.name}`}
              style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', background: out ? '#E7E5E4' : C.orange, color: out ? C.faint : C.orangeDeep, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconPlus size={20} />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

// ------------------------------------------------------------------ modifier sheet

function groupHint(g) {
  if (g.required) return g.max_selections > 1 ? `Required · choose ${g.min_selections}–${g.max_selections}` : 'Required · choose 1';
  return g.max_selections ? `Optional · up to ${g.max_selections}` : 'Optional';
}

function SeatPicker({ seats, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {Array.from({ length: seats }, (_, i) => i + 1).map((n) => {
        const on = value === n;
        return (
          <button key={n} type="button" aria-pressed={on} onClick={() => onChange(n)} className="c29-press"
            style={{ width: 48, height: 48, borderRadius: 12, border: on ? `2px solid ${C.ink}` : `1px solid ${C.border}`, background: on ? C.ink : '#FFFFFF', color: on ? '#FFFFFF' : C.ink2, fontSize: 16, fontWeight: on ? 700 : 600 }}>{n}</button>
        );
      })}
      <button type="button" aria-pressed={value === null} onClick={() => onChange(null)} className="c29-press"
        style={{ height: 48, padding: '0 12px', borderRadius: 12, border: value === null ? `2px solid ${C.ink}` : `1px solid ${C.border}`, background: value === null ? C.ink : '#FFFFFF', color: value === null ? '#FFFFFF' : C.ink2, fontSize: 14, fontWeight: 600 }}>Share</button>
    </div>
  );
}

function ItemSheet({ item, seats, seat, onSeat, course, onAdd, onClose }) {
  const [chosen, setChosen] = useState({});
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [allergy, setAllergy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { setChosen({}); setQty(1); setNote(''); setAllergy(null); setError(null); }, [item]);
  if (!item) return null;
  const toggle = (g, o) => {
    setChosen((cur) => {
      const list = cur[g.id] || [];
      if (list.includes(o.id)) return { ...cur, [g.id]: list.filter((x) => x !== o.id) };
      if (g.max_selections === 1) return { ...cur, [g.id]: [o.id] };
      if (g.max_selections && list.length >= g.max_selections) return cur;
      return { ...cur, [g.id]: [...list, o.id] };
    });
  };
  const missing = item.modifier_groups.filter((g) => (chosen[g.id] || []).length < g.min_selections);
  const unit = item.price_cents + item.modifier_groups.reduce((sum, g) => sum + g.options.filter((o) => (chosen[g.id] || []).includes(o.id)).reduce((a, o) => a + o.price_cents, 0), 0);
  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await onAdd({
        menu_item_id: item.id, quantity: qty, modifier_option_ids: Object.values(chosen).flat(),
        notes: joinNotes(note, allergy), seat_number: seat, course,
      });
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open label={`${item.name} options`} onClose={onClose} width={600} top>
      <ModalHead title={item.name} sub={[item.description, money(item.price_cents)].filter(Boolean).join(' · ')} onClose={onClose} />
      <div className="c29-scroll" style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {item.modifier_groups.map((g) => {
          const picked = chosen[g.id] || [];
          const done = picked.length >= g.min_selections && (g.required ? picked.length > 0 : true);
          return (
            <section key={g.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{g.name}</span>
                <Chip bg={g.required ? C.orangeSoft : '#F5F5F4'} fg={g.required ? C.orangeInk : C.ink3} height={22}>{groupHint(g)}</Chip>
                <span style={{ flex: 1 }} />
                {g.required && done && <span style={{ color: C.greenInk }} aria-label="Done"><IconCheck size={18} /></span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                {g.options.map((o) => {
                  const on = picked.includes(o.id);
                  return (
                    <button key={o.id} type="button" aria-pressed={on} onClick={() => toggle(g, o)} className="c29-press"
                      style={{ minHeight: 52, padding: '6px 10px', borderRadius: 12, border: on ? `2px solid ${C.orange}` : `1px solid ${C.border}`, background: on ? C.orangeTint : '#FFFFFF', fontSize: 14, fontWeight: on ? 700 : 600, color: on ? '#9A3412' : C.ink2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.2, gap: 2 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{on && g.max_selections !== 1 && <IconCheck size={15} />}{o.name}</span>
                      {o.price_cents > 0 && <span style={{ fontSize: 12, fontWeight: 600 }}>+{money(o.price_cents)}</span>}
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 700 }}>Seat</span>
            <SeatPicker seats={seats} value={seat} onChange={onSeat} />
          </section>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 200 }}>
            <span style={{ fontSize: 15, fontWeight: 700 }}>Special request</span>
            <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="e.g. sauce on the side" style={inputStyle} />
          </label>
        </div>
        {allergy === null ? (
          <button type="button" onClick={() => setAllergy('')} style={{ alignSelf: 'flex-start', height: 40, padding: '0 12px', borderRadius: 10, border: '1px dashed #FCA5A5', background: '#FEF2F2', color: C.redInk, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconAlert size={15} sw={2.4} />Add allergy for the kitchen
          </button>
        ) : (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: C.redInk, letterSpacing: '0.04em' }}>ALLERGY · shown red on the kitchen screen</span>
            <input autoFocus value={allergy} maxLength={150} onChange={(e) => setAllergy(e.target.value)} placeholder="e.g. peanut, clean pan" style={{ ...inputStyle, borderColor: '#FCA5A5', background: '#FEF2F2' }} />
          </label>
        )}
        {missing.length > 0 && <span style={{ fontSize: 13, color: C.orangeInk, fontWeight: 600 }}>Choose {missing.map((g) => g.name).join(', ')} to add it.</span>}
        {error && <ErrorNote light error={error} compact />}
      </div>
      <div style={{ padding: '14px 24px 20px', display: 'flex', alignItems: 'center', gap: 14, borderTop: `1px solid ${C.hair}`, background: C.cream }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#FFFFFF', border: `1px solid ${C.border}`, borderRadius: 14, padding: 4 }}>
          <button type="button" aria-label="Fewer" onClick={() => setQty((q) => Math.max(1, q - 1))} style={{ width: 44, height: 44, border: 'none', borderRadius: 10, background: C.sand, color: C.ink, fontSize: 22, fontWeight: 600 }}>−</button>
          <span style={{ width: 36, textAlign: 'center', fontSize: 18, fontWeight: 800 }}>{qty}</span>
          <button type="button" aria-label="More" onClick={() => setQty((q) => Math.min(99, q + 1))} style={{ width: 44, height: 44, border: 'none', borderRadius: 10, background: C.sand, color: C.ink, fontSize: 22, fontWeight: 600 }}>+</button>
        </div>
        <button type="button" className="c29-press" onClick={add} disabled={busy || missing.length > 0}
          style={{ flex: 1, height: 56, border: 'none', borderRadius: 16, background: C.orange, color: C.orangeDeep, fontSize: 17, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 22px', opacity: missing.length ? 0.5 : 1 }}>
          <span>{busy ? 'Adding…' : 'Add to order'}</span><span>{money(unit * qty)}</span>
        </button>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ a sent line: change or void

const VOID_REASONS = ['Guest changed their mind', 'Wrong item entered', 'Kitchen error', 'Quality - remade'];

function SentItemSheet({ item, seats, onClose, run, sessionId, sessionVersion, onDone, needsCode = () => false }) {
  const toast = useToast();
  const [mode, setMode] = useState('change');
  const [seat, setSeat] = useState(item?.seat_number ?? null);
  const [course, setCourse] = useState(item?.course ?? null);
  const parts = splitNotes(item?.notes);
  const [note, setNote] = useState(parts.note || '');
  const [allergy, setAllergy] = useState(parts.allergy || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!item) return null;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { seat_number: seat, course, notes: joinNotes(note, allergy), expected_version: sessionVersion };
      const ok = await run((token) => api.patch(`/dining/sessions/${sessionId}/items/${item.id}`, { ...body, approval_token: token }),
        { action: 'orders.modify_sent', entityId: item.id, sessionId, what: item.name });
      if (ok) { toast.ok(`${item.name} updated - the kitchen sees the change`); onDone(); onClose(); }
    } catch (e) {
      setError(errorText(e));
      if (e.isStale) onDone();
    } finally {
      setBusy(false);
    }
  };
  const voidIt = async () => {
    setBusy(true);
    setError(null);
    try {
      const ok = await run((token) => api.post(`/dining/sessions/${sessionId}/items/${item.id}/void`, { reason: reason.trim(), approval_token: token, expected_version: sessionVersion }),
        { action: 'items.void', entityId: item.id, sessionId, what: item.name, reason: reason.trim() });
      if (ok) { toast.ok(`${item.name} voided`); onDone(); onClose(); }
    } catch (e) {
      setError(errorText(e));
      if (e.isStale) onDone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open label={`${item.name} - sent`} onClose={onClose} width={560}>
      <ModalHead title={`${item.quantity > 1 ? `${item.quantity}× ` : ''}${item.name}`} sub={`Sent ${clock(item.sent_at)} · Round ${item.round_no} · ${money(item.total_cents)}`} onClose={onClose} />
      <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Segmented value={mode} onChange={setMode} options={[{ value: 'change', label: 'Change seat / course / note' }, { value: 'void', label: 'Void item' }]} />
        {mode === 'change' && (
          <>
            <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}><span style={{ fontSize: 15, fontWeight: 700 }}>Seat</span><SeatPicker seats={seats} value={seat} onChange={setSeat} /></section>
            <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>Course</span>
              <Segmented value={course} onChange={setCourse} options={[{ value: null, label: 'None' }, ...COURSES.map((c) => ({ value: c.value, label: `${c.value} · ${c.label}` }))]} />
            </section>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><span style={{ fontSize: 15, fontWeight: 700 }}>Note</span><input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} style={inputStyle} /></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><span style={{ fontSize: 13, fontWeight: 800, color: C.redInk }}>ALLERGY</span><input value={allergy} maxLength={150} onChange={(e) => setAllergy(e.target.value)} placeholder="None" style={{ ...inputStyle, borderColor: '#FCA5A5', background: '#FEF2F2' }} /></label>
            <span style={{ fontSize: 12.5, color: C.muted }}>Quantity and options of a sent item cannot change - void it and add a new one.</span>
          </>
        )}
        {mode === 'void' && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {VOID_REASONS.map((r) => (
                <button key={r} type="button" aria-pressed={reason === r} onClick={() => setReason(r)} className="c29-press"
                  style={{ height: 46, padding: '0 14px', borderRadius: 12, border: reason === r ? `2px solid ${C.redHi}` : `1px solid ${C.border}`, background: reason === r ? '#FEF2F2' : '#FFFFFF', color: reason === r ? C.redInk : C.ink2, fontSize: 14, fontWeight: 600 }}>{r}</button>
              ))}
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><span style={{ fontSize: 15, fontWeight: 700 }}>Reason</span><input value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder="Why is it voided?" style={inputStyle} /></label>
            <span style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>The kitchen ticket keeps the line struck through and marked VOIDED. It counts in no total.{needsCode('items.void') ? ' An owner’s or manager’s approval code is needed.' : ''}</span>
          </>
        )}
        {error && <ErrorNote light error={error} compact />}
      </div>
      <ModalFoot>
        <Btn kind="white" height={56} onClick={onClose}>Cancel</Btn>
        {mode === 'change'
          ? <Btn kind="ink" height={56} busy={busy} onClick={save}>Save change</Btn>
          : <Btn kind="danger" height={56} busy={busy} disabled={!reason.trim()} onClick={voidIt}>Void item</Btn>}
      </ModalFoot>
    </Modal>
  );
}

// ------------------------------------------------------------------ the order (right)

function Line({ item, chip, onClick, children, draft }) {
  const parts = splitNotes(item.notes);
  const mods = [modifierText(item), parts.note].filter(Boolean).join(' · ');
  const voided = item.status === 'voided';
  return (
    <div style={{ display: 'flex', alignItems: draft ? 'center' : 'flex-start', gap: 10, padding: draft ? '6px 0' : '7px 0', borderBottom: draft ? 'none' : `1px solid ${C.hair2}` }}>
      <span style={{ minWidth: 26, height: 22, padding: '0 3px', flex: 'none', borderRadius: 6, background: draft ? '#FFFFFF' : C.sand, color: draft ? '#9A3412' : C.ink3, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.seat_number ? `S${item.seat_number}` : 'All'}</span>
      <button type="button" onClick={onClick} disabled={!onClick} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', border: 'none', background: 'transparent', padding: 0, textAlign: 'left', color: 'inherit', cursor: onClick ? 'pointer' : 'default', minHeight: 36, justifyContent: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: draft ? 700 : 600, color: voided ? C.faint : (draft ? C.ink : C.ink2), textDecoration: voided ? 'line-through' : 'none' }}>
          {!draft && item.quantity > 1 ? `${item.quantity}× ` : ''}{item.name}
        </span>
        {mods && <span style={{ fontSize: 12, color: draft ? '#9A3412' : C.faint }}>{mods}</span>}
        {parts.allergy && <span style={{ fontSize: 12, fontWeight: 800, color: C.redInk }}>ALLERGY · {parts.allergy}</span>}
        {voided && item.void_reason && <span style={{ fontSize: 12, color: C.redInk, fontWeight: 600 }}>Voided · {item.void_reason}</span>}
      </button>
      {chip && <Chip bg={chip.bg} fg={chip.fg} dot={chip.dot} height={22} style={{ fontSize: 11.5 }}>{chip.label}</Chip>}
      {children}
      <span style={{ width: 62, textAlign: 'right', fontSize: 14, fontWeight: draft ? 700 : 600, color: voided ? C.faint : C.ink3, flex: 'none', textDecoration: voided ? 'line-through' : 'none' }}>{money(item.total_cents)}</span>
    </div>
  );
}

// ------------------------------------------------------------------ the screen

export default function OrderPadScreen() {
  const { sessionId } = useParams();
  const sid = Number(sessionId);
  const { me, perms } = useDiningSession();
  const navigate = useNavigate();
  const toast = useToast();
  const { run, dialog } = useApproval();
  const [soundOn] = useSoundPref();
  const [tray, setTray] = useState(false);
  const alertsState = useAlerts({ enabled: alertsEnabledFor(me) });
  const canKds = perms.can('kds.read');
  const view = useLive(async () => {
    const session = await api.get(`/dining/sessions/${sid}`);
    let tickets = null;
    if (canKds && session?.order?.id) {
      tickets = await api.get('/dining/kitchen/tickets', { order_id: session.order.id, view: 'all' }).then((d) => d.tickets).catch(() => null);
    }
    return { ...session, tickets };
  }, [sid, canKds], {
    match: (e) => e.payload?.session_id === sid || (e.payload?.ticket && e.payload.ticket.session_id === sid) || e.type === 'session.merged' || e.type === 'session.transferred',
  });
  const menu = useLive(() => api.get('/dining/menu'), [], { match: () => false });
  const [cat, setCat] = useState(null);
  const [query, setQuery] = useState('');
  const [course, setCourse] = useState(null);
  const [seat, setSeat] = useState(1);
  const [sheetItem, setSheetItem] = useState(null);
  const [sentItem, setSentItem] = useState(null);
  const [busy, setBusy] = useState(null);
  const [justSent, setJustSent] = useState(null);
  const sendKey = useRef(null);

  const data = view.data;
  const session = data?.session;
  const categories = useMemo(() => menu.data?.categories || [], [menu.data]);
  const activeCat = categories.find((c) => c.id === cat) || categories[0];
  const allItems = useMemo(() => categories.flatMap((c) => c.items), [categories]);
  const soldOut = allItems.filter((i) => !i.available).length;
  const shown = query.trim()
    ? allItems.filter((i) => `${i.id} ${i.name} ${i.description || ''}`.toLowerCase().includes(query.trim().toLowerCase()))
    : (activeCat?.items || []);
  const seats = Math.max(1, Math.min(session?.party_size || 1, 20));
  const canAdd = perms.can('orders.create') && session && session.status !== 'closed';
  const ticketItem = useMemo(() => {
    const map = {};
    (data?.tickets || []).forEach((tk) => tk.items.forEach((it) => { map[it.order_item_id] = it; }));
    return map;
  }, [data]);

  useEffect(() => { if (session && seat && seat > seats) setSeat(1); }, [session, seat, seats]);

  const addLine = useCallback(async (line) => {
    await api.post(`/dining/sessions/${sid}/items`, { client_line_id: newKey('line').slice(0, 64), ...line });
    view.reload();
  }, [sid, view]);

  const quickAdd = async (item) => {
    try {
      await addLine({ menu_item_id: item.id, quantity: 1, modifier_option_ids: [], seat_number: seat, course });
      toast.ok(`${item.name} added`);
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const setQty = async (item, qty) => {
    setBusy(`q${item.id}`);
    try {
      if (qty < 1) await api.del(`/dining/sessions/${sid}/items/${item.id}`);
      else await api.patch(`/dining/sessions/${sid}/items/${item.id}`, { quantity: qty });
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
      view.reload();
    }
  };

  const send = async () => {
    setBusy('send');
    sendKey.current = sendKey.current || newKey('send');
    try {
      const answer = await api.post(`/dining/sessions/${sid}/send`, {}, { idempotencyKey: sendKey.current });
      sendKey.current = null;
      setJustSent({ at: Date.now(), round: answer.round_no, count: answer.sent_item_ids.length, stations: (answer.kitchen?.tickets || []).map((t) => t.station_name).filter(Boolean) });
      setTimeout(() => setJustSent(null), 6000);
    } catch (e) {
      if (!e.network) sendKey.current = null; // a network drop retries with the same key: the kitchen never gets it twice
      toast.error(errorText(e));
    } finally {
      setBusy(null);
      view.reload();
    }
  };

  const requestCheck = async () => {
    setBusy('check');
    try {
      await api.post(`/dining/sessions/${sid}/request-checkout`, {});
      toast.ok('Check requested - the front desk sees it');
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
      view.reload();
    }
  };

  if (view.error && !data) {
    const gone = view.error.status === 404;
    return (
      <div className="c29 c29-light" style={{ height: '100vh', display: 'flex' }}>
        <LeftRail me={me} active="order" streamStatus={view.streamStatus} />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FullError light error={gone ? 'This table is not open any more.' : view.error} title={gone ? 'Table not found' : 'Could not load this table'}
            onRetry={gone ? () => navigate('/dining/floor') : view.reload} />
        </div>
      </div>
    );
  }

  const draft = data?.draft || [];
  const rounds = data?.rounds || [];
  const totals = data?.totals;
  const closed = session?.status === 'closed';
  const sendLabel = justSent && !draft.length ? `Sent to kitchen · ${nowClock(new Date(justSent.at))}`
    : draft.length ? `Send to Kitchen (${draft.length})` : 'Nothing new to send';

  return (
    <div className="c29 c29-light" style={{ height: '100vh', display: 'flex', overflow: 'hidden' }}>
      <LeftRail me={me} active="order" streamStatus={view.streamStatus} extra={alertsState.enabled && <AlertBell dark={false} count={alertsState.alerts.length} onClick={() => setTray(true)} />} />

      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: '16px 20px 0', gap: 14, position: 'relative' }}>
        <StreamBanner status={view.streamStatus} light onRetry={view.reload} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label htmlFor="menuSearch" style={{ flex: 1, height: 48, borderRadius: 14, background: '#FFFFFF', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', color: C.faint }}>
            <IconSearch size={18} />
            <input id="menuSearch" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search dishes or type an item number"
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 15, color: C.ink, minWidth: 0 }} />
            {query && <button type="button" aria-label="Clear search" onClick={() => setQuery('')} style={{ border: 'none', background: 'transparent', color: C.faint, display: 'flex', minHeight: 32, alignItems: 'center' }}><IconClose size={16} /></button>}
          </label>
          {soldOut > 0 && <span style={{ height: 36, padding: '0 12px', borderRadius: 10, background: '#FEF2F2', color: C.redInk, fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>{soldOut} sold out</span>}
          <Clock size={20} />
        </div>
        {!query && categories.length > 0 && (
          <div role="tablist" aria-label="Menu categories" className="c29-scroll" style={{ display: 'flex', gap: 8, flex: 'none', paddingBottom: 2 }}>
            {categories.map((c) => {
              const on = c.id === activeCat?.id;
              return (
                <button key={c.id} type="button" role="tab" aria-selected={on} onClick={() => setCat(c.id)} className="c29-press"
                  style={{ height: 48, padding: '0 18px', borderRadius: 24, border: `1px solid ${on ? C.ink : C.border}`, background: on ? C.ink : '#FFFFFF', color: on ? '#FFFFFF' : C.ink2, fontSize: 15, fontWeight: on ? 700 : 600, display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', flex: 'none' }}>
                  {c.name}<span style={{ color: on ? '#FDBA74' : C.faint, fontSize: 13 }}>{c.items.length}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className="c29-scroll" style={{ flex: 1, minHeight: 0, paddingBottom: 20 }}>
          {menu.loading && !menu.data && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(196px, 1fr))', gap: 14 }}>
              {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={232} style={{ borderRadius: 18 }} />)}
            </div>
          )}
          {menu.error && !menu.data && <FullError light error={menu.error} onRetry={menu.reload} title="Could not load the menu" />}
          {menu.data && !allItems.length && <Empty light icon={<IconOrder size={28} />} title="The menu is empty" body="Dishes added in Back Office → Menu show here." />}
          {menu.data && allItems.length > 0 && !shown.length && <Empty light icon={<IconSearch size={26} />} title="No dish matches" body={`Nothing on the menu matches “${query}”.`} />}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(196px, 1fr))', gap: 14, alignContent: 'start' }}>
            {shown.map((item) => <MenuTile key={item.id} item={item} canAdd={canAdd} onOpen={setSheetItem} onQuickAdd={quickAdd} />)}
          </div>
        </div>
      </main>

      <aside style={{ width: 388, flex: 'none', background: '#FFFFFF', borderLeft: '1px solid #ECE6DF', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 20px 12px', display: 'flex', flexDirection: 'column', gap: 12, borderBottom: `1px solid ${C.hair}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <h1 style={{ margin: 0, fontFamily: FONT_TIGHT, fontSize: 26, fontWeight: 800, letterSpacing: '-0.4px' }}>{session ? `Table ${session.table_number}` : <Skeleton height={30} style={{ width: 120 }} />}</h1>
              {session && <span style={{ fontSize: 13, color: C.muted }}>{plural(session.party_size, 'guest')}{session.party_size > 1 ? ` · Seats 1–${session.party_size}` : ''}{session.server_name ? ` · ${shortName(session.server_name)}` : ''}</span>}
            </div>
            <button type="button" onClick={() => navigate(session ? `/dining/floor?table=${session.table_id}` : '/dining/floor')} className="c29-press"
              style={{ height: 44, padding: '0 14px', borderRadius: 12, border: `1px solid ${C.border}`, background: '#FFFFFF', color: C.ink2, fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
              <IconSwap size={16} />Change table
            </button>
          </div>
          {canAdd && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.faint, width: 52 }}>Course</span>
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 4, padding: 4, background: C.sand, borderRadius: 12 }}>
                {COURSES.map((c) => {
                  const on = c.value === course;
                  return (
                    <button key={c.value} type="button" aria-pressed={on} onClick={() => setCourse(on ? null : c.value)}
                      style={{ height: 38, border: 'none', borderRadius: 9, background: on ? '#FFFFFF' : 'transparent', color: on ? C.ink : C.ink3, fontSize: 13, fontWeight: on ? 700 : 600, boxShadow: on ? '0 1px 3px rgba(28,25,23,.12)' : 'none', whiteSpace: 'nowrap' }}>{c.value} · {c.label}</button>
                  );
                })}
              </div>
            </div>
          )}
          {canAdd && seats > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.faint, width: 52 }}>Seat</span>
              <div className="c29-scroll" style={{ flex: 1, display: 'flex', gap: 4, padding: 4, background: C.sand, borderRadius: 12, overflowY: 'hidden' }}>
                {[null, ...Array.from({ length: Math.min(seats, 12) }, (_, i) => i + 1)].map((n) => {
                  const on = n === seat;
                  return (
                    <button key={String(n)} type="button" aria-pressed={on} aria-label={n ? `Seat ${n}` : 'Shared by the table'} onClick={() => setSeat(n)}
                      style={{ minWidth: 38, height: 38, padding: '0 8px', border: 'none', borderRadius: 9, background: on ? C.ink : 'transparent', color: on ? '#FFFFFF' : C.ink3, fontSize: 13, fontWeight: 700, flex: 'none' }}>{n || 'All'}</button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="c29-scroll" style={{ flex: 1, minHeight: 0, padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {view.loading && !data && <><Skeleton height={20} /><Skeleton height={44} /><Skeleton height={44} /></>}
          {closed && <ErrorNote light error="This table was released. Nothing more can be added." compact />}
          {rounds.map((r) => (
            <div key={r.round_no} style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', color: C.muted, textTransform: 'uppercase', padding: '6px 0 2px' }}>
                <IconLock size={13} />Sent {clock(r.sent_at)} · Round {r.round_no}
              </div>
              {r.items.map((item) => {
                const k = item.status === 'voided' ? 'voided' : (ticketItem[item.id]?.status || 'sent');
                return (
                  <Line key={item.id} item={item} chip={LINE_CHIP[k] || LINE_CHIP.sent}
                    onClick={item.status !== 'voided' && (perms.canWithApproval('items.void') || perms.canWithApproval('orders.modify_sent')) ? () => setSentItem(item) : undefined} />
                );
              })}
            </div>
          ))}
          {draft.length > 0 && (
            <div style={{ marginTop: rounds.length ? 10 : 0, borderRadius: 16, background: C.orangeTint, border: '1.5px dashed #FDBA74', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', color: C.orangeInk, textTransform: 'uppercase' }}>
                <span>New · not sent yet</span>
              </div>
              {draft.map((item) => (
                <div key={item.id}>
                  {item.course && <div style={{ fontSize: 11, fontWeight: 700, color: '#9A3412', opacity: 0.8 }}>{courseLabel(item.course)}</div>}
                  <Line item={item} draft>
                    {canAdd && <Stepper value={item.quantity} label={item.name} busy={busy === `q${item.id}`} onDec={() => setQty(item, item.quantity - 1)} onInc={() => setQty(item, item.quantity + 1)} />}
                  </Line>
                </div>
              ))}
            </div>
          )}
          {data && !rounds.length && !draft.length && (
            <Empty light icon={<IconOrder size={26} />} title="Nothing ordered yet" body={canAdd ? 'Tap a dish to add it. It stays here until you send it to the kitchen.' : 'Items appear here when the server adds them.'} style={{ padding: '40px 8px' }} />
          )}
        </div>

        <div style={{ padding: '12px 20px 18px', borderTop: `1px solid ${C.hair}`, display: 'flex', flexDirection: 'column', gap: 10, background: C.cream }}>
          {totals?.error ? (
            <ErrorNote light compact error={typeof totals.error === 'string' ? totals.error : 'The bill cannot be totalled yet.'} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, color: C.ink3 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>{money(totals?.subtotal_cents)}</span></div>
              {(totals?.discount_cents > 0 || totals?.comp_cents > 0) && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Discount</span><span style={{ color: C.greenInk }}>−{money((totals.discount_cents || 0) + (totals.comp_cents || 0)).replace('−', '')}</span></div>}
              {totals?.service_charge_cents > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Service charge</span><span>{money(totals.service_charge_cents)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tax{totals?.tax_rate_percent != null ? ` ${Number(totals.tax_rate_percent)}%` : ''}</span><span>{money(totals?.tax_cents)}</span></div>
              {totals?.paid_cents > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Paid</span><span style={{ color: C.greenInk }}>{money(totals.paid_cents)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', color: C.ink, marginTop: 2 }}>
                <span style={{ fontSize: 16, fontWeight: 700 }}>{totals?.paid_cents > 0 ? 'Due' : 'Total'}</span>
                <span style={{ fontFamily: FONT_TIGHT, fontSize: 26, fontWeight: 800 }}>{money(totals?.paid_cents > 0 ? totals?.due_cents : totals?.total_cents)}</span>
              </div>
            </div>
          )}
          {perms.can('orders.send') && (
            <button type="button" className="c29-press" onClick={send} disabled={!draft.length || busy === 'send' || closed}
              style={{ height: 60, border: 'none', borderRadius: 16, background: draft.length ? C.orange : '#E7F6EC', color: draft.length ? C.orangeDeep : C.greenInk, fontSize: 18, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, cursor: draft.length ? 'pointer' : 'default' }}>
              {draft.length ? <IconSend size={20} /> : <IconCheck size={20} />}{busy === 'send' ? 'Sending…' : sendLabel}
            </button>
          )}
          {justSent && justSent.stations.length > 0 && !draft.length && (
            <span style={{ fontSize: 12.5, color: C.greenInk, textAlign: 'center', fontWeight: 600 }}>Round {justSent.round} · {plural(justSent.count, 'item')} · {justSent.stations.join(', ')}</span>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: perms.can('payments.take') ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 8 }}>
            <Btn kind="white" height={50} busy={busy === 'check'} disabled={!session || closed || session.status === 'checkout_requested' || !(perms.can('orders.create') || perms.can('payments.take'))}
              onClick={requestCheck}>{session?.status === 'checkout_requested' ? 'Check requested' : 'Request check'}</Btn>
            {perms.can('payments.take') && <Btn kind="ink" height={50} disabled={!session} onClick={() => navigate(`/dining/checkout/${sid}`)}>Proceed to pay</Btn>}
          </div>
        </div>
      </aside>

      {sheetItem && <ItemSheet item={sheetItem} seats={seats} seat={seat} onSeat={setSeat} course={course} onAdd={addLine} onClose={() => setSheetItem(null)} />}
      {sentItem && (
        <SentItemSheet item={sentItem} seats={seats} run={run} needsCode={perms.needsCode} sessionId={sid} sessionVersion={session?.version}
          onClose={() => setSentItem(null)} onDone={() => view.reload()} />
      )}
      {dialog}
      <AlertCenter me={me} state={alertsState} trayOpen={tray} onTray={setTray} soundOn={soundOn} streamStatus={view.streamStatus} dark={false} />
    </div>
  );
}
